import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  addToast,
} from "@heroui/react"
import { Trans, useLingui } from "@lingui/react/macro"
import { Eye } from "lucide-react"

import { openFile } from "#/toolbar/file"

import { ICON_SIZE } from "../utils"

export function ViewStateDropdown({
  onImport,
  getExport,
  compact,
  onCompactChange,
}: {
  onImport(json: object): void
  getExport(): object
  compact: boolean
  onCompactChange(compact: boolean): void
}) {
  const { t } = useLingui()
  return (
    <Dropdown>
      <DropdownTrigger>
        <Button isIconOnly title="Filters" variant="light">
          <Eye size={ICON_SIZE} />
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label="Static Actions">
        <DropdownSection showDivider title={t`Filter settings`}>
          <DropdownItem
            key="import-file"
            onPress={async () => {
              try {
                const jsonText = await openFile(".json")
                const json = JSON.parse(await jsonText.text())
                onImport(json)

                addToast({
                  title: "Imported from file",
                  description: "Successfully imported from file",
                })
              } catch (e) {
                addToast({
                  title: "Failed to import",
                  description: (e as Error).message,
                  color: "danger",
                })
                // eslint-disable-next-line no-console
                console.error(e)
              }
            }}
          >
            <Trans>Import from file</Trans>
          </DropdownItem>
          <DropdownItem
            key="import-clipboard"
            onPress={async () => {
              try {
                const jsonText = await navigator.clipboard.readText()
                const json = JSON.parse(jsonText)
                onImport(json)

                addToast({
                  title: "Imported from clipboard",
                  description: "Successfully imported from clipboard",
                  color: "success",
                })
              } catch (e) {
                addToast({
                  title: "Failed to import from clipboard",
                  description: (e as Error).message,
                  color: "danger",
                })
                alert((e as Error).message)
              }
            }}
          >
            <Trans>Import from clipboard</Trans>
          </DropdownItem>
          <DropdownItem
            key="restore-file"
            onPress={() => {
              try {
                const json = getExport()

                const jsonText = JSON.stringify(json)
                const blob = new Blob([jsonText], { type: "application/json" })
                const url = URL.createObjectURL(blob)
                const a = document.createElement("a")
                a.href = url
                a.download = "harViewState.json"
                a.click()
                URL.revokeObjectURL(url)
              } catch (e) {
                addToast({
                  title: "Failed to export to file",
                  description: (e as Error).message,
                  color: "danger",
                })
              }
            }}
          >
            <Trans>Export to file</Trans>
          </DropdownItem>
          <DropdownItem
            key="restore-clipboard"
            onPress={async () => {
              try {
                const json = getExport()
                await navigator.clipboard.writeText(JSON.stringify(json))
                addToast({
                  title: "Filters exported to clipboard",
                  description: "You can now paste it in another HAR viewer.",
                  color: "success",
                })
              } catch (e) {
                addToast({
                  title: "Failed to export to clipboard",
                  description: (e as Error).message,
                  color: "danger",
                })
              }
            }}
          >
            <Trans>Export to clipboard</Trans>
          </DropdownItem>
        </DropdownSection>

        <DropdownItem
          isDisabled
          key="compact"
          onPress={() => {
            onCompactChange(!compact)
          }}
        >
          {compact ? "Disable compact mode" : "Enable compact mode"}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  )
}
